import { useTranslation } from 'react-i18next';
import {
  DENUNCIA_CATEGORIAS,
  SUBCATEGORIAS_DISCRIMINACAO,
  type DenunciaCategoria,
  type SubcategoriaDiscriminacao,
} from '@aions/core';

interface Props {
  selected: DenunciaCategoria | null;
  onSelect: (cat: DenunciaCategoria) => void;
  /** Sub-tipos opcionais. Aplicável apenas quando selected === discriminacao. */
  subcategoriasDiscriminacao: ReadonlyArray<SubcategoriaDiscriminacao>;
  onSubcategoriasChange: (next: ReadonlyArray<SubcategoriaDiscriminacao>) => void;
}

// Exportado para o teste de consistência (CATEGORY_ICONS == DENUNCIA_CATEGORIAS
// == CHECK 019 == chaves i18n). O tipo Record<DenunciaCategoria,…> já garante em
// compile-time que as chaves são exatamente as 12 categorias.
export const CATEGORY_ICONS: Record<DenunciaCategoria, string> = {
  assedio_moral: '🚫',
  assedio_sexual: '⚠️',
  discriminacao: '🏳️',
  discriminacao_salarial_genero: '💸',
  violencia_no_trabalho: '👊',
  corrupcao: '💰',
  fraude: '🎭',
  conflito_interesse: '⚖️',
  seguranca_saude: '🦺',
  violacao_dados_lgpd: '🔓',
  violacao_conduta_etica: '📖',
  outros: '🗂️',
};

const CATEGORIAS_COM_SUBTIPOS: ReadonlySet<DenunciaCategoria> = new Set(['discriminacao']);

export function Step1Categoria({
  selected,
  onSelect,
  subcategoriasDiscriminacao,
  onSubcategoriasChange,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const showSubtipos = selected !== null && CATEGORIAS_COM_SUBTIPOS.has(selected);

  const toggleSubtipo = (sub: SubcategoriaDiscriminacao): void => {
    const has = subcategoriasDiscriminacao.includes(sub);
    if (has) {
      onSubcategoriasChange(subcategoriasDiscriminacao.filter((s) => s !== sub));
    } else {
      onSubcategoriasChange([...subcategoriasDiscriminacao, sub]);
    }
  };

  return (
    <div className="fade-in">
      <h2>{t('wizard.step1_title')}</h2>
      <p style={{ color: 'var(--color-text-secondary)', margin: '8px 0 0' }}>
        {t('step1.instruction')}
      </p>

      <div className="category-grid" role="radiogroup" aria-label={t('wizard.step1_title')}>
        {DENUNCIA_CATEGORIAS.map((cat) => (
          <label
            key={cat}
            className={`category-card ${selected === cat ? 'selected' : ''}`}
            id={`category-${cat}`}
          >
            <input
              type="radio"
              name="categoria"
              value={cat}
              checked={selected === cat}
              onChange={() => {
                onSelect(cat);
                // Se trocou para categoria sem sub-tipos, limpa o array para
                // evitar carregar sub-tipos de uma categoria anterior.
                if (!CATEGORIAS_COM_SUBTIPOS.has(cat) && subcategoriasDiscriminacao.length > 0) {
                  onSubcategoriasChange([]);
                }
              }}
            />
            <span className="category-radio-dot" />
            <span>
              {CATEGORY_ICONS[cat]} {t(`categorias.${cat}`)}
            </span>
          </label>
        ))}
      </div>

      {!selected && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', textAlign: 'center' }}>
          {t('step1.required')}
        </p>
      )}

      {showSubtipos && (
        <fieldset
          style={{
            marginTop: 24,
            padding: '16px 20px',
            border: '1px solid var(--surface-cream-deep, #e6dfd2)',
            borderRadius: 'var(--radius-md, 6px)',
            background: 'var(--surface-cream-light, #faf6ee)',
          }}
        >
          <legend
            style={{
              padding: '0 8px',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
            }}
          >
            {t('subcategorias_discriminacao.label')}
          </legend>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginTop: 8,
            }}
            role="group"
          >
            {SUBCATEGORIAS_DISCRIMINACAO.map((sub) => {
              const checked = subcategoriasDiscriminacao.includes(sub);
              return (
                <label
                  key={sub}
                  className={`subcategoria-chip ${checked ? 'selected' : ''}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: 16,
                    border: `1px solid ${checked ? 'var(--brand-sage, #7FA89B)' : 'var(--surface-cream-deep, #e6dfd2)'}`,
                    background: checked ? 'var(--brand-sage, #7FA89B)' : 'transparent',
                    color: checked ? 'white' : 'var(--color-text-primary)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    name={`subcategoria-${sub}`}
                    checked={checked}
                    onChange={() => {
                      toggleSubtipo(sub);
                    }}
                    style={{ accentColor: 'var(--brand-sage, #7FA89B)' }}
                  />
                  {t(`subcategorias_discriminacao.${sub}`)}
                </label>
              );
            })}
          </div>
        </fieldset>
      )}
    </div>
  );
}
